import { useRef, useState, useEffect } from "react";
import { confirmDialog } from "../components/ConfirmDialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, ExternalLink, Sparkles, Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import TickerCombobox, { findTicker } from "../components/TickerCombobox";
import CoverImageDropzone from "../components/CoverImageDropzone";
import FeeCombobox from "../components/FeeCombobox";
import { RISK_PROFILES } from "../lib/portfolioMeta";

// Devises proposées pour le prix d'un mouvement (la devise trouvée automatiquement s'ajoute si besoin)
const PRICE_CURRENCIES = ["USD", "EUR", "GBP", "CHF", "JPY", "CAD", "AUD", "HKD"];

const TIERS = [
  { id: "free", label: "Gratuit" },
  { id: "essentiel", label: "Essentiel" },
  { id: "pro", label: "Pro" },
  { id: "max", label: "Max" },
];

const SOURCE_LABELS = { coinbase: "Coinbase", bitpanda: "Bitpanda" };
const sourceLabel = (s) => SOURCE_LABELS[s] || null;

function SourceBadge({ source, testid }) {
  const label = sourceLabel(source);
  const isManual = !label;
  return (
    <span
      data-testid={testid}
      className={
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] border " +
        (isManual
          ? "bg-white/[0.04] text-zinc-400 border-white/10"
          : "bg-sky-500/10 text-sky-300 border-sky-500/30")
      }
    >
      <FileSpreadsheet className="w-3 h-3" />
      {isManual ? "Saisie manuelle" : `Source : ${label}`}
    </span>
  );
}

const EMPTY_PORTFOLIO = {
  name: "",
  slug: "",
  description: "",
  cover_image: "",
  tier_required: "free",
  published: true,
  strategy_text: "",
  horizon: "",
  risk_profile: "",
  rebalancing: "",
};

const EMPTY_TX = {
  symbol: "",
  asset_type: "crypto",
  action: "DEPOSIT",
  quantity: "",
  price: "",
  price_currency: "USD",
  date: new Date().toISOString().slice(0, 10),
  time: new Date().toTimeString().slice(0, 8),
  tz: "Europe/Paris",
  fees: 0,
  note: "",
  external_id: "",
  _priceMatched: null,
};

const _STABLE_SUFFIXES = ["-USD", "-EUR", "-USDT", "-USDC"];
function canonSym(sym) {
  let s = (sym || "").trim().toUpperCase();
  for (const suf of _STABLE_SUFFIXES) {
    if (s.endsWith(suf) && s.length > suf.length) { s = s.slice(0, -suf.length); break; }
  }
  return s;
}
function fmtQty(v) {
  if (v === null || v === undefined || isNaN(v)) return "0";
  return Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 8 });
}
function _fmtDateFr(d) {
  if (!d) return "";
  const parts = String(d).split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
}

function PortfolioDialog({ open, onOpenChange, initial, onSubmit, submitting }) {
  const [form, setForm] = useState(initial || EMPTY_PORTFOLIO);
  const bind = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-card border-white/10 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Modifier le portefeuille" : "Nouveau portefeuille Side B"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>Nom</Label>
            <Input value={form.name} onChange={bind("name")} data-testid="sideb-form-name" />
          </div>
          <div className="grid gap-2">
            <Label>Slug (optionnel)</Label>
            <Input value={form.slug} onChange={bind("slug")} placeholder="auto" data-testid="sideb-form-slug" />
          </div>
          <div className="grid gap-2">
            <Label>Description</Label>
            <Textarea value={form.description || ""} onChange={bind("description")} rows={3} data-testid="sideb-form-description" />
          </div>
          {/* Section « La stratégie » affichée sur la page du portefeuille (tout est facultatif) */}
          <div className="grid gap-3 rounded-lg border border-white/10 p-4">
            <div>
              <div className="text-sm font-medium">La stratégie</div>
              <p className="text-xs text-zinc-500 mt-0.5">
                Affichée sur la page du portefeuille, même pour les visiteurs non abonnés. Laissez vide pour la masquer.
              </p>
            </div>
            <div className="grid gap-2">
              <Label>Explication</Label>
              <Textarea
                value={form.strategy_text || ""}
                onChange={bind("strategy_text")}
                rows={4}
                placeholder="Objectif du portefeuille, logique de sélection des actifs, gestion du risque…"
                data-testid="sideb-form-strategy"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Horizon conseillé</Label>
                <Input value={form.horizon || ""} onChange={bind("horizon")} placeholder="ex. 5 ans et plus" data-testid="sideb-form-horizon" />
              </div>
              <div className="grid gap-2">
                <Label>Profil de risque</Label>
                <Select value={form.risk_profile || "none"} onValueChange={(v) => setForm({ ...form, risk_profile: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="sideb-form-risk"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non renseigné</SelectItem>
                    {RISK_PROFILES.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Rééquilibrage</Label>
              <Input
                value={form.rebalancing || ""}
                onChange={bind("rebalancing")}
                placeholder="ex. Chaque trimestre, si un actif s'écarte de 10 points de sa cible"
                data-testid="sideb-form-rebalancing"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Image de couverture</Label>
            <CoverImageDropzone
              value={form.cover_image || ""}
              onChange={(url) => setForm({ ...form, cover_image: url })}
              testid="sideb-portfolio-cover"
            />
          </div>
          <div className="grid gap-2">
            <Label>Palier requis</Label>
            <Select value={form.tier_required} onValueChange={(v) => setForm({ ...form, tier_required: v })}>
              <SelectTrigger data-testid="sideb-form-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIERS.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
            <Label className="cursor-pointer">Publié</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Annuler</Button>
          <Button
            onClick={() => {
              if (!form.name.trim()) return toast.error("Nom requis");
              onSubmit(form);
            }}
            disabled={submitting}
            className="bg-emerald-500 text-black hover:bg-emerald-300"
            data-testid="sideb-form-submit"
          >
            {submitting ? "…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageTxDialog({ portfolio, open, onOpenChange }) {
  const qc = useQueryClient();
  const [tx, setTx] = useState(EMPTY_TX);
  const fileRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [priceFetching, setPriceFetching] = useState(false);

  const { data: txs = [], refetch } = useQuery({
    queryKey: ["sideb-txs", portfolio?.id],
    queryFn: async () => (await api.get(`/admin/sideb/portfolios/${portfolio.id}/transactions`)).data,
    enabled: !!portfolio,
  });

  const addMut = useMutation({
    mutationFn: async (payload) => (await api.post(`/admin/sideb/portfolios/${portfolio.id}/transactions`, payload)).data,
    onSuccess: () => {
      toast.success("Mouvement ajouté — followers notifiés");
      setTx(EMPTY_TX);
      refetch();
      qc.invalidateQueries({ queryKey: ["sideb-portfolios-admin"] });
      qc.invalidateQueries({ queryKey: ["sideb-balances", portfolio?.id] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (tid) => (await api.delete(`/admin/sideb/portfolios/${portfolio.id}/transactions/${tid}`)).data,
    onSuccess: () => {
      toast.success("Supprimé");
      refetch();
      qc.invalidateQueries({ queryKey: ["sideb-balances", portfolio?.id] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const clearImportedMut = useMutation({
    mutationFn: async () => (await api.delete(`/admin/sideb/portfolios/${portfolio.id}/transactions/imported`)).data,
    onSuccess: (data) => {
      toast.success(`${data.deleted || 0} mouvement(s) importé(s) supprimé(s)`);
      setClearOpen(false);
      resetCsv();
      refetch();
      qc.invalidateQueries({ queryKey: ["sideb-portfolios-admin"] });
      qc.invalidateQueries({ queryKey: ["sideb-balances", portfolio?.id] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  // Soldes disponibles à la date/heure du formulaire (pour Withdraw/Swap)
  const balAt = tx.date && tx.time ? `${tx.date}T${tx.time}` : tx.date;
  const { data: balances = [] } = useQuery({
    queryKey: ["sideb-balances", portfolio?.id, balAt],
    queryFn: async () =>
      (await api.get(`/admin/sideb/portfolios/${portfolio.id}/balances`, { params: { at: balAt } })).data,
    enabled: !!portfolio && !!tx.date,
  });
  const balMap = {};
  for (const b of balances) balMap[b.symbol] = b;
  const { data: incons } = useQuery({
    queryKey: ["sideb-incons", portfolio?.id, txs.length],
    queryFn: async () => (await api.get(`/admin/sideb/portfolios/${portfolio.id}/inconsistencies`)).data,
    enabled: !!portfolio,
  });
  const needsHeld = tx.action === "WITHDRAW" || tx.action === "SWAP";
  const available = balMap[canonSym(tx.symbol)]?.quantity ?? 0;
  const feeSameAsset = true; // frais exprimés sur l'actif débité (modèle % → actif)
  const qtyNum = Number(String(tx.quantity).replace(",", ".")) || 0;
  const feePct = Number(tx.fees) || 0;
  const feeQty = feeSameAsset ? (feePct / 100) * qtyNum : 0;
  const isCashLeg = ["USD", "EUR", "GBP", "CHF"].includes(canonSym(tx.symbol));
  const overAvailable =
    needsHeld && !isCashLeg && (qtyNum + feeQty) > available + 1e-9;
  const nothingHeld = needsHeld && balances.length === 0;

  // Si l'actif choisi n'est plus détenu à la nouvelle date, vider le champ
  useEffect(() => {
    if (!needsHeld || isCashLeg) return;
    if (tx.symbol && !balMap[canonSym(tx.symbol)]) {
      setTx((cur) => ({ ...cur, symbol: "" }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balAt, tx.action]);

  // Auto-fetch price when symbol + date + time change
  useEffect(() => {
    if (!tx.symbol || !tx.date || !tx.time) return;
    const t = setTimeout(async () => {
      try {
        setPriceFetching(true);
        const iso = `${tx.date}T${tx.time}`;
        const res = await api.get("/market/price-at", {
          params: { symbol: tx.symbol, timestamp: iso, asset_type: tx.asset_type },
        });
        setTx((cur) => {
          const keep = cur.price && cur._priceSymbol === tx.symbol && cur._priceAt === iso;
          return {
          ...cur,
          price: keep ? cur.price : res.data.price,
          // Devise du prix trouvé (ex. JPY pour une action de Tokyo)
          price_currency: keep ? cur.price_currency : res.data.currency || "USD",
          _priceMatched: res.data.matched_at,
          _priceSymbol: tx.symbol,
          _priceAt: iso,
          _priceInterval: res.data.interval,
          };
        });
      } catch (e) {
        setTx((cur) => ({ ...cur, _priceMatched: null }));
      } finally {
        setPriceFetching(false);
      }
    }, 450);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx.symbol, tx.date, tx.time, tx.asset_type]);

  const submit = () => {
    if (!tx.symbol?.trim() || !tx.quantity) return toast.error("Actif et quantité requis");
    const qty = Number(String(tx.quantity).replace(",", "."));
    const px = Number(String(tx.price).replace(",", "."));
    if (tx.action === "SWAP" && (!px || px <= 0)) {
      return toast.error("Prix requis pour un Swap");
    }
    if (tx.action === "SWAP" && !tx.counter_symbol) {
      return toast.error("Actif reçu requis");
    }
    if (tx.action === "SWAP" && !tx.counter_quantity) {
      return toast.error("Quantité reçue requise pour un swap");
    }
    // Prix obligatoire pour DEPOSIT/WITHDRAW d'un actif non-cash
    if ((tx.action === "DEPOSIT" || tx.action === "WITHDRAW") && !isCashLeg && (!px || px <= 0)) {
      return toast.error("Indiquez le prix (prix de vente pour un retrait)");
    }
    if (overAvailable) {
      return toast.error(`Quantité supérieure au disponible (${fmtQty(available)} ${canonSym(tx.symbol)})`);
    }
    const feesPct = Number(tx.fees) || 0;
    const feesAbs = +((feesPct / 100) * qty * (px || 1)).toFixed(6);
    addMut.mutate({
      symbol: tx.symbol,
      asset_type: tx.asset_type,
      action: tx.action,
      quantity: qty,
      price: px || 0,
      price_currency: tx.price_currency || "USD",
      date: tx.date,
      time: tx.time,
      tz: tx.tz,
      fees: feesAbs,
      counter_symbol: tx.counter_symbol || null,
      counter_asset_type: tx.counter_symbol ? tx.counter_asset_type || null : null,
      counter_quantity: tx.counter_quantity ? Number(String(tx.counter_quantity).replace(",", ".")) : null,
      external_id: tx.external_id || null,
      note: tx.note || null,
    });
  };

  const onPickFile = () => fileRef.current?.click();

  // Sélection : on mémorise le File et on VIDE l'input immédiatement
  // (sinon re-choisir le même fichier ne déclenche pas onChange → import « bloqué »).
  const onFileChange = () => {
    const file = fileRef.current?.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setCsvFile(file);
    uploadCsv(true, file);
  };

  const resetCsv = () => {
    setCsvPreview(null);
    setCsvFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadCsv = async (dryRun, fileArg) => {
    const file = fileArg || csvFile;
    if (!file) return;
    try {
      setCsvImporting(true);
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(`/admin/sideb/portfolios/${portfolio.id}/transactions/import-csv?dry_run=${dryRun}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (dryRun) {
        setCsvPreview(res.data);
      } else {
        toast.success(`${res.data.inserted || 0} mouvement(s) importé(s)${res.data.refreshed ? ` · ${res.data.refreshed} mis à jour` : ""} · ${res.data.duplicate_count} doublon(s) ignoré(s)`);
        resetCsv();
        refetch();
        qc.invalidateQueries({ queryKey: ["sideb-portfolios-admin"] });
      }
    } catch (e) {
      toast.error("Échec de l'import : " + (e?.response?.data?.detail || e.message));
      resetCsv();
    } finally {
      setCsvImporting(false);
    }
  };

  if (!portfolio) return null;

  const currentTicker = findTicker(tx.symbol) || (tx._tickerInfo
    ? { name: tx._tickerInfo.name, symbol: tx.symbol, group: tx._tickerInfo.exchange || "Yahoo Finance" }
    : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl bg-card border-white/10 max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 flex-wrap">
              Mouvements — {portfolio.name}
              <SourceBadge source={portfolio.import_source} testid="sideb-dialog-source-badge" />
            </span>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onFileChange} data-testid="sideb-csv-input" />
              <Button
                variant="outline"
                size="sm"
                onClick={onPickFile}
                disabled={csvImporting}
                className="border-white/10 bg-white/[0.03]"
                data-testid="sideb-csv-pick"
                title={portfolio.import_source
                  ? `Ce portefeuille n'accepte que des CSV ${sourceLabel(portfolio.import_source)}`
                  : 'Le fichier doit être un export Bitpanda ou Coinbase'}
              >
                {csvImporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}
                {portfolio.import_source
                  ? `Importer un CSV ${sourceLabel(portfolio.import_source)}`
                  : "Importer un CSV (Bitpanda / Coinbase)"}
              </Button>
              {portfolio.import_source && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClearOpen(true)}
                  disabled={csvImporting || clearImportedMut.isPending}
                  className="border-rose-500/30 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10"
                  data-testid="sideb-csv-clear"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Vider les mouvements importés
                </Button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {csvPreview && (
          <Card className="p-4 bg-emerald-500/5 border border-emerald-500/20 space-y-3" data-testid="sideb-csv-preview">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-zinc-200">Aperçu de l'import</span>
                {csvPreview.source && (
                  <span
                    className="ml-2 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] uppercase tracking-[0.15em] border border-emerald-500/30"
                    data-testid="csv-source"
                  >
                    Source : {csvPreview.source}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs flex-wrap">
                <span className="text-emerald-300 font-mono-num" data-testid="csv-new-count">{csvPreview.new_count} nouveaux</span>
                <span className="text-amber-300 font-mono-num" data-testid="csv-dup-count">{csvPreview.duplicate_count} déjà présents</span>
                {csvPreview.refresh_count > 0 && (
                  <span className="text-sky-300 font-mono-num" data-testid="csv-refresh-count">{csvPreview.refresh_count} corrigés</span>
                )}
                <span className="text-zinc-400 font-mono-num" data-testid="csv-ignored-count">{csvPreview.ignored_count || 0} lignes ignorées</span>
                <span className="text-rose-300 font-mono-num" data-testid="csv-err-count">{csvPreview.error_count} erreurs</span>
              </div>
            </div>
            {csvPreview.new_count === 0 && !csvPreview.refresh_count && (
              <div className="text-[12px] text-zinc-300 p-2 rounded border border-white/10 bg-white/[0.03]" data-testid="csv-nothing-new">
                Rien de nouveau dans ce fichier — tous les mouvements sont déjà présents.
              </div>
            )}
            {csvPreview.new_by_action && (
              <div className="flex items-center gap-3 text-[11px] text-zinc-400">
                <span>Détail des nouveaux mouvements :</span>
                <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 font-mono-num" data-testid="csv-new-deposit">
                  Deposit · {csvPreview.new_by_action.DEPOSIT || 0}
                </span>
                <span className="px-2 py-0.5 rounded bg-rose-500/10 text-rose-300 font-mono-num" data-testid="csv-new-withdraw">
                  Withdraw · {csvPreview.new_by_action.WITHDRAW || 0}
                </span>
                <span className="px-2 py-0.5 rounded bg-sky-500/10 text-sky-300 font-mono-num" data-testid="csv-new-swap">
                  Swap · {csvPreview.new_by_action.SWAP || 0}
                </span>
              </div>
            )}
            {csvPreview.refresh_count > 0 && (
              <div className="text-[11px] text-sky-300" data-testid="csv-refresh-note">
                {csvPreview.refresh_count} ligne(s) existante(s) seront mises à jour (correction du parseur).
              </div>
            )}
            {csvPreview.negative_balances?.length > 0 && (
              <div className="text-[11px] text-orange-300 p-2 rounded border border-orange-500/30 bg-orange-500/5" data-testid="csv-negative-balances">
                <span className="font-semibold">Soldes négatifs détectés (historique peut-être incomplet) :</span>{" "}
                {csvPreview.negative_balances.map((n, i) => (
                  <span key={i}>{n.symbol} le {(_fmtDateFr(n.date))} ({fmtQty(n.balance)}){i < csvPreview.negative_balances.length - 1 ? " · " : ""}</span>
                ))}
              </div>
            )}
            {csvPreview.errors?.length > 0 && (
              <details className="text-[11px]">
                <summary className="cursor-pointer text-rose-300"><AlertTriangle className="w-3 h-3 inline mr-1" /> Voir les erreurs et types inconnus ({csvPreview.errors.length})</summary>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {csvPreview.errors.map((e, i) => (
                    <div key={i} className="text-zinc-400">Ligne {e.row} : {e.reason}</div>
                  ))}
                </div>
              </details>
            )}
            <div className="max-h-40 overflow-y-auto border border-white/5 rounded">
              <table className="w-full text-[11px]">
                <thead className="bg-white/[0.03] text-zinc-500">
                  <tr>
                    <th className="text-left px-2 py-1">Date</th>
                    <th className="text-left px-2 py-1">Heure</th>
                    <th className="text-left px-2 py-1">Actif</th>
                    <th className="text-left px-2 py-1">Action</th>
                    <th className="text-right px-2 py-1">Qté</th>
                    <th className="text-right px-2 py-1">Prix</th>
                    <th className="text-left px-2 py-1">Contre-partie</th>
                    <th className="text-right px-2 py-1">État</th>
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.preview.slice(0, 50).map((p, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-2 py-1 text-zinc-300">{p.date}</td>
                      <td className="px-2 py-1 text-zinc-400 font-mono-num">{p.time}</td>
                      <td className="px-2 py-1 text-emerald-300 font-mono-num">{p.symbol}</td>
                      <td className={`px-2 py-1 ${p.action === "DEPOSIT" ? "text-emerald-400" : p.action === "WITHDRAW" ? "text-rose-400" : "text-sky-400"}`}>{p.action}</td>
                      <td className="px-2 py-1 text-right font-mono-num">{p.quantity}</td>
                      <td className="px-2 py-1 text-right font-mono-num">{p.price}</td>
                      <td className="px-2 py-1 text-[10px] text-zinc-400 font-mono-num">
                        {p.counter_symbol ? `${p.counter_quantity} ${p.counter_symbol}` : "—"}
                      </td>
                      <td className="px-2 py-1 text-right text-[10px]">
                        {p.status === "update"
                          ? <span className="text-sky-400">corrigé</span>
                          : p.duplicate
                            ? <span className="text-amber-400">déjà présent</span>
                            : <span className="text-emerald-400">nouveau</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={resetCsv}>Annuler</Button>
              <Button
                size="sm"
                onClick={() => uploadCsv(false)}
                disabled={csvImporting || (csvPreview.new_count === 0 && !csvPreview.refresh_count)}
                className="bg-emerald-500 text-black hover:bg-emerald-300"
                data-testid="sideb-csv-confirm"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" /> Confirmer l'import ({csvPreview.new_count}{csvPreview.refresh_count > 0 ? ` +${csvPreview.refresh_count} maj` : ""})
              </Button>
            </div>
          </Card>
        )}

        {incons && (incons.negative || (incons.suspicious_price && incons.suspicious_price.length > 0)) && (
          <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5" data-testid="sideb-inconsistencies">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-amber-300 mb-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Incohérences
            </div>
            {incons.negative && (
              <div className="text-[12px] text-amber-200/90 mb-2" data-testid="sideb-incons-negative">
                Solde {incons.negative.symbol} négatif le {_fmtDateFr(incons.negative.date)} ({fmtQty(incons.negative.balance)}) — mouvement {incons.negative.action} {fmtQty(incons.negative.quantity)}. Corrigez le mouvement fautif ci-dessous.
              </div>
            )}
            {incons.suspicious_price?.length > 0 && (
              <div className="space-y-1">
                <div className="text-[11px] text-amber-200/70">Mouvements sans prix (0 ou 1) — probablement saisis sans prix :</div>
                {incons.suspicious_price.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 text-[12px] text-amber-100/90" data-testid={`sideb-incons-price-${s.id}`}>
                    <span>
                      {_fmtDateFr(s.date)} · <span className={s.action === "WITHDRAW" ? "text-rose-300" : "text-emerald-300"}>{s.action}</span> {fmtQty(s.quantity)} {s.symbol} @ {s.price}
                    </span>
                    <Button
                      variant="ghost" size="sm"
                      className="text-rose-400 hover:bg-rose-500/10 h-6"
                      onClick={async () => (await confirmDialog({ title: "Supprimer ce mouvement ?", description: "La performance du portefeuille sera recalculée sans lui." })) && delMut.mutate(s.id)}
                      data-testid={`sideb-incons-del-${s.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid gap-3 p-4 rounded-lg border border-white/10 bg-white/[0.02]">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="grid gap-1 md:col-span-3">
              <Label className="text-xs">Actif</Label>
              {needsHeld ? (
                <>
                  <Select
                    value={tx.symbol || ""}
                    onValueChange={(sym) => {
                      const found = findTicker(sym);
                      const nextType = found?.group === "Crypto" ? "crypto"
                        : found?.group === "Matières premières" ? "commodity"
                        : found?.group === "Obligations" ? "bond"
                        : ["USD", "EUR"].includes(sym) ? "cash" : "crypto";
                      setTx((cur) => ({ ...cur, symbol: sym, asset_type: nextType }));
                    }}
                  >
                    <SelectTrigger data-testid="sideb-tx-held-select">
                      <SelectValue placeholder={nothingHeld ? "Aucun actif détenu" : "Choisir un actif détenu…"} />
                    </SelectTrigger>
                    <SelectContent>
                      {balances.map((b) => (
                        <SelectItem key={b.symbol} value={b.symbol}>
                          {b.symbol} — {fmtQty(b.quantity)} disponible
                        </SelectItem>
                      ))}
                      {tx.action === "SWAP" && (
                        <>
                          <SelectItem value="USD">USD (payé par carte/virement)</SelectItem>
                          <SelectItem value="EUR">EUR (payé par carte/virement)</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  {nothingHeld && tx.action === "WITHDRAW" && (
                    <span className="text-[11px] text-amber-300" data-testid="sideb-tx-nothing-held">
                      Aucun actif détenu à cette date — faites d'abord un Deposit
                    </span>
                  )}
                </>
              ) : (
                <TickerCombobox
                  remote
                  value={tx.symbol}
                  onChange={(sym, info) => {
                    const found = findTicker(sym);
                    // Actif trouvé sur Yahoo (hors liste) : type donné par la recherche
                    const nextType = !found ? info?.asset_type || "stock"
                      : found.group === "Crypto" ? "crypto"
                      : found.group === "Matières premières" ? "commodity"
                      : found.group === "Obligations" ? "bond"
                      : "stock";
                    setTx((cur) => ({ ...cur, symbol: sym, asset_type: nextType, _tickerInfo: found ? null : info }));
                  }}
                  placeholder="Chercher par nom ou ticker…"
                  testid="sideb-tx-ticker-combo"
                />
              )}
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">Type</Label>
              <Select value={tx.action} onValueChange={(v) => setTx({ ...tx, action: v })}>
                <SelectTrigger data-testid="sideb-tx-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DEPOSIT">Deposit</SelectItem>
                  <SelectItem value="WITHDRAW">Withdraw</SelectItem>
                  <SelectItem value="SWAP">Swap</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1 md:col-span-2">
              <Label className="text-xs">Date / Heure (Europe/Paris)</Label>
              <div className="flex gap-1">
                <Input type="date" value={tx.date} onChange={(e) => setTx({ ...tx, date: e.target.value })} data-testid="sideb-tx-date" />
                <Input type="time" step="1" value={tx.time} onChange={(e) => setTx({ ...tx, time: e.target.value })} data-testid="sideb-tx-time" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="grid gap-1 md:col-span-2">
              <Label className="text-xs flex items-center justify-between">
                <span>Quantité</span>
                {needsHeld && !isCashLeg && tx.symbol && (
                  <button
                    type="button"
                    className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                    data-testid="sideb-tx-max"
                    onClick={() => {
                      const maxQty = feeSameAsset
                        ? available / (1 + feePct / 100)
                        : available;
                      setTx((cur) => ({ ...cur, quantity: String(+maxQty.toFixed(8)) }));
                    }}
                  >Max</button>
                )}
              </Label>
              <Input
                inputMode="decimal"
                placeholder="ex. 0,00125"
                value={tx.quantity}
                onChange={(e) => setTx({ ...tx, quantity: e.target.value })}
                data-testid="sideb-tx-qty"
                className={overAvailable ? "border-rose-500 focus-visible:ring-rose-500" : ""}
              />
              {needsHeld && !isCashLeg && tx.symbol && (
                overAvailable ? (
                  <span className="text-[11px] text-rose-400" data-testid="sideb-tx-qty-error">
                    Quantité supérieure au disponible ({fmtQty(available)} {canonSym(tx.symbol)})
                  </span>
                ) : (
                  <span className="text-[11px] text-zinc-500" data-testid="sideb-tx-available">
                    Disponible : {fmtQty(available)} {canonSym(tx.symbol)}
                  </span>
                )
              )}
            </div>
            <div className="grid gap-1 md:col-span-2">
              <Label className="text-xs flex items-center justify-between">
                <span>{tx.action === "WITHDRAW" ? "Prix de vente unitaire" : tx.action === "DEPOSIT" ? "Prix d'achat unitaire" : "Prix unitaire"}</span>
                {priceFetching ? (
                  <span className="text-[9px] text-zinc-500"><Loader2 className="w-3 h-3 inline animate-spin" /> récupération…</span>
                ) : tx._priceMatched && (
                  <span className="text-[9px] text-emerald-400" data-testid="sideb-tx-price-matched">
                    ≈ {new Date(tx._priceMatched).toLocaleTimeString("fr-FR")}
                  </span>
                )}
              </Label>
              <div className="flex gap-1">
                <Input
                  inputMode="decimal"
                  placeholder="auto"
                  value={tx.price}
                  onChange={(e) => setTx({ ...tx, price: e.target.value })}
                  data-testid="sideb-tx-price"
                />
                <Select value={tx.price_currency || "USD"} onValueChange={(v) => setTx({ ...tx, price_currency: v })}>
                  <SelectTrigger className="w-[72px]" data-testid="sideb-tx-price-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...new Set([...PRICE_CURRENCIES, tx.price_currency || "USD"])].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(tx.action === "WITHDRAW" || tx.action === "DEPOSIT") && tx.quantity && tx.price && (
                <span className="text-[11px] text-zinc-500" data-testid="sideb-tx-gross">
                  {tx.action === "WITHDRAW" ? "Montant récupéré" : "Montant investi"} : {fmtQty(qtyNum)} × {tx.price} = {(qtyNum * Number(String(tx.price).replace(",", "."))).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} {tx.price_currency || "USD"}
                </span>
              )}
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">Frais (%)</Label>
              <FeeCombobox
                value={tx.fees}
                onChange={(v) => setTx({ ...tx, fees: v })}
                placeholder="0 %"
                testid="sideb-tx-fees"
              />
            </div>
            <div className="grid gap-1 items-end">
              <Label className="text-xs text-zinc-500">Total</Label>
              <div className="px-3 py-2 rounded-md border border-white/10 bg-black/40 text-right text-sm font-mono-num text-zinc-200" data-testid="sideb-tx-total">
                {tx.quantity && tx.price
                  ? (() => {
                      const qty = Number(String(tx.quantity).replace(",", "."));
                      const px = Number(String(tx.price).replace(",", "."));
                      const feesPct = Number(tx.fees) || 0;
                      const feesAbs = (feesPct / 100) * qty * px;
                      const total = qty * px + feesAbs * (tx.action === "DEPOSIT" ? 1 : -1);
                      return total.toLocaleString("fr-FR", { maximumFractionDigits: 2 });
                    })()
                  : "—"}
              </div>
            </div>
          </div>
          {tx.action === "SWAP" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-white/5" data-testid="sideb-tx-counter-row">
              <div className="grid gap-1 md:col-span-2">
                <Label className="text-xs">Reçu (actif cible)</Label>
                <TickerCombobox
                  remote
                  value={tx.counter_symbol || ""}
                  onChange={(sym, info) => {
                    // Type de l'actif reçu (action, crypto…) : sinon le calcul le prendrait pour une crypto
                    const found = findTicker(sym);
                    const counterType = !found ? info?.asset_type || "stock"
                      : found.group === "Crypto" ? "crypto"
                      : found.group === "Matières premières" ? "commodity"
                      : found.group === "Obligations" ? "bond"
                      : found.group === "Monnaies & Stablecoins" ? (["USD", "EUR", "CHF"].includes(sym) ? "cash" : "crypto")
                      : "stock";
                    setTx({ ...tx, counter_symbol: sym, counter_asset_type: counterType });
                  }}
                  placeholder="ex. SOL, ETH, USDC…"
                  testid="sideb-tx-counter-combo"
                />
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Quantité reçue</Label>
                <Input
                  inputMode="decimal"
                  placeholder="ex. 4.975"
                  value={tx.counter_quantity || ""}
                  onChange={(e) => setTx({ ...tx, counter_quantity: e.target.value })}
                  data-testid="sideb-tx-counter-qty"
                />
              </div>
            </div>
          )}
          {currentTicker && (
            <div className="text-[11px] text-zinc-500">
              Sélection : <span className="text-zinc-200">{currentTicker.name}</span>
              <span className="ml-2 font-mono-num text-emerald-300">{currentTicker.symbol}</span>
              <span className="ml-2 text-zinc-500">· {currentTicker.group}</span>
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={submit}
              disabled={addMut.isPending || overAvailable || nothingHeld}
              className="bg-emerald-500 text-black hover:bg-emerald-300"
              data-testid="sideb-tx-submit"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Ajouter le mouvement
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">Historique</div>
          {txs.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500 border border-white/5 rounded-lg">Aucun mouvement pour l'instant.</div>
          ) : (
            <div className="border border-white/5 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02]">
                  <tr className="text-left text-xs uppercase tracking-[0.15em] text-zinc-500">
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Symbole</th>
                    <th className="px-3 py-2">Action</th>
                    <th className="px-3 py-2 text-right">Qté</th>
                    <th className="px-3 py-2 text-right">Prix</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((t) => (
                    <tr key={t.id} className="border-t border-white/5" data-testid={`sideb-tx-row-${t.id}`}>
                      <td className="px-3 py-2 font-mono-num text-zinc-400">{t.date}</td>
                      <td className="px-3 py-2 font-mono-num text-emerald-300">{t.symbol}</td>
                      <td className="px-3 py-2">
                        <span className={t.action === "DEPOSIT" ? "text-emerald-400" : t.action === "WITHDRAW" ? "text-rose-400" : "text-sky-400"}>{t.action}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num">{t.quantity}</td>
                      <td className="px-3 py-2 text-right font-mono-num">
                        {t.price}{t.price_currency && t.price_currency !== "USD" ? ` ${t.price_currency}` : ""}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-400 hover:bg-rose-500/10 h-7"
                          onClick={async () => (await confirmDialog({ title: "Supprimer ce mouvement ?", description: "La performance du portefeuille sera recalculée sans lui." })) && delMut.mutate(t.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>

      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="max-w-md bg-card border-white/10" data-testid="sideb-clear-confirm">
          <DialogHeader>
            <DialogTitle>Vider les mouvements importés ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400">
            Tous les mouvements importés par CSV de « {portfolio.name} » seront supprimés
            et la source ({sourceLabel(portfolio.import_source) || "—"}) sera réinitialisée.
            Les mouvements saisis à la main sont conservés. Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setClearOpen(false)} data-testid="sideb-clear-cancel">
              Annuler
            </Button>
            <Button
              size="sm"
              className="bg-rose-500 text-white hover:bg-rose-400"
              onClick={() => clearImportedMut.mutate()}
              disabled={clearImportedMut.isPending}
              data-testid="sideb-clear-confirm-btn"
            >
              {clearImportedMut.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
              Vider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default function AdminSideBPortfoliosPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [txPortfolio, setTxPortfolio] = useState(null);

  const { data: portfolios = [], isLoading } = useQuery({
    queryKey: ["sideb-portfolios-admin"],
    queryFn: async () => (await api.get("/sideb/portfolios")).data,
  });

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/admin/sideb/portfolios", payload)).data,
    onSuccess: () => {
      toast.success("Portefeuille créé");
      qc.invalidateQueries({ queryKey: ["sideb-portfolios-admin"] });
      setDlgOpen(false);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, payload }) => (await api.patch(`/admin/sideb/portfolios/${id}`, payload)).data,
    onSuccess: () => {
      toast.success("Enregistré");
      qc.invalidateQueries({ queryKey: ["sideb-portfolios-admin"] });
      setDlgOpen(false);
      setEditing(null);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/sideb/portfolios/${id}`)).data,
    onSuccess: () => {
      toast.success("Portefeuille supprimé");
      qc.invalidateQueries({ queryKey: ["sideb-portfolios-admin"] });
      qc.invalidateQueries({ queryKey: ["sideb-portfolios"] });
    },
    // Toujours afficher la raison d'un échec (avant : rien ne s'affichait)
    onError: (e) =>
      toast.error(
        `Suppression impossible : ${
          e?.response ? `${formatApiErrorDetail(e.response.data?.detail)} (code ${e.response.status})` : `le serveur ne répond pas (${e?.message || "erreur réseau"})`
        }`,
        { duration: 10000 },
      ),
  });

  const startCreate = () => {
    setEditing(null);
    setDlgOpen(true);
  };
  const startEdit = (p) => {
    setEditing(p);
    setDlgOpen(true);
  };
  const submit = (payload) => {
    if (editing) updateMut.mutate({ id: editing.id, payload });
    else createMut.mutate(payload);
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Admin</div>
          <h1 className="font-display font-black text-3xl">Portefeuilles Side B</h1>
        </div>
        <Button
          onClick={startCreate}
          className="bg-emerald-500 text-black hover:bg-emerald-300"
          data-testid="sideb-admin-new"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nouveau portefeuille
        </Button>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">Chargement…</div>
      ) : portfolios.length === 0 ? (
        <Card className="p-12 text-center bg-card border-white/5">
          <p className="text-zinc-500">Aucun portefeuille Side B. Créez-en un pour publier vos positions et notifier vos followers à chaque mouvement.</p>
          <Button className="mt-4 bg-emerald-500 text-black hover:bg-emerald-300" onClick={startCreate}>
            <Plus className="w-4 h-4 mr-1.5" /> Créer le premier portefeuille
          </Button>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="sideb-admin-list">
          {portfolios.map((p) => (
            <Card key={p.id} className="p-4 bg-card border-white/5 flex items-center gap-3" data-testid={`sideb-admin-row-${p.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium">{p.name}</h3>
                  {p.tier_required !== "free" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] uppercase">
                      <Sparkles className="w-3 h-3" /> {p.tier_required}
                    </span>
                  )}
                  {!p.published && (
                    <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">brouillon</span>
                  )}
                  <SourceBadge source={p.import_source} testid={`sideb-admin-source-${p.slug}`} />
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  /portefeuilles-sideb/{p.slug} · {p.followers_count} followers
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setTxPortfolio(p)} className="border-white/10" data-testid={`sideb-admin-manage-${p.slug}`}>
                Mouvements
              </Button>
              <Button variant="ghost" size="sm" onClick={() => window.open(`/portefeuilles-sideb/${p.slug}`, "_blank")}>
                <ExternalLink className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => startEdit(p)} data-testid={`sideb-admin-edit-${p.slug}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-rose-400 hover:bg-rose-500/10"
                onClick={async () =>
                  (await confirmDialog({
                    title: `Supprimer « ${p.name} » ?`,
                    description: `Le portefeuille disparaît du site et ${p.followers_count ? `ses ${p.followers_count} abonnés ne le suivent plus` : "personne ne pourra plus le suivre"}. Ses mouvements restent dans votre historique. Action définitive.`,
                  })) && deleteMut.mutate(p.id)
                }
                disabled={deleteMut.isPending}
                data-testid={`sideb-admin-delete-${p.slug}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </Card>
          ))}
        </div>
      )}

      {dlgOpen && (
        <PortfolioDialog
          open={dlgOpen}
          onOpenChange={(v) => {
            setDlgOpen(v);
            if (!v) setEditing(null);
          }}
          initial={editing}
          onSubmit={submit}
          submitting={createMut.isPending || updateMut.isPending}
        />
      )}
      <ManageTxDialog
        portfolio={txPortfolio}
        open={!!txPortfolio}
        onOpenChange={(v) => !v && setTxPortfolio(null)}
      />
    </div>
  );
}
